import { Component, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  ReactiveFormsModule,
  Validators,
  FormControl,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { FormField, FormSelect, FormTextarea } from '@one-of-one/form';
import { ArrowLeft, Lamp, Trash } from '@one-of-one/icons';
import {
  Pack,
  PACKS,
  Profession,
  RegistrationDto,
  RegistrationType,
} from '@one-of-one/models';
import { Reservation } from '../../../services/reservation';
import { Footer } from '@one-of-one/footer';

@Component({
  selector: 'app-group',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    FormField,
    ArrowLeft,
    FormSelect,
    Lamp,
    Trash,
    Footer,
    FormTextarea,
  ],
  templateUrl: './group.html',
})
export class Group {
  private readonly fb = inject(FormBuilder);
  private readonly location = inject(Location);
  private readonly reservService = inject(Reservation);
  private readonly router = inject(Router);

  isSubmitting = signal(false);
  twinState = signal(0);

  groupForm = this.fb.group(
    {
      company: this.fb.group(
        {
          name: ['', Validators.required],
          ice: ['', Validators.required],
          address: ['', Validators.required],
          city: ['', Validators.required],
          country: ['', Validators.required],
        },
        { updateOn: 'submit' },
      ),
      representative: this.fb.group(
        {
          fullName: ['', Validators.required],
          role: ['', Validators.required],
          phoneNumber: [
            '',
            [Validators.required, Validators.pattern('^[- +()0-9]+$')],
          ],
          email: ['', [Validators.required, Validators.email]],
        },
        { updateOn: 'submit' },
      ),
      participants: this.fb.array([]),
      comment: ['', [Validators.maxLength(300)]],
    },
    { updateOn: 'submit' },
  );

  professions = [
    {
      label: 'Médecin',
      value: Profession.DOCTOR,
    },
    {
      label: 'Non médecin',
      value: Profession.NON_DOCTOR,
    },
    {
      label: 'Résident',
      value: Profession.RESIDENT,
    },
  ];

  packOptions = PACKS;

  constructor() {
    this.addParticipant();

    this.participants.valueChanges.subscribe(() => {
      this.twinState.update((v) => v + 1);
    });
  }

  get participants() {
    return this.groupForm.get('participants') as FormArray;
  }

  get withoutAccomodation() {
    return this.packOptions.find((pack) => pack.id === Pack.PACK_1)!;
  }

  get packTwin() {
    return this.packOptions.find((pack) => pack.id === Pack.PACK_SUPP)!;
  }

  get otherPacks() {
    return this.packOptions.slice(2, PACKS.length + 1);
  }

  get company() {
    return this.groupForm.get('company') as FormGroup;
  }

  get representative() {
    return this.groupForm.get('representative') as FormGroup;
  }

  get totalAmount(): number {
    const selectedPacks = this.participants.value.map((participant: any) => {
      return participant.pack;
    });

    return selectedPacks.reduce((total: number, packId: Pack) => {
      const pack = this.packOptions.find((p) => p.id === packId);
      return total + (pack?.price ?? 0);
    }, 0);
  }

  getParticipantControl(name: string, index: number): FormControl {
    return this.participants.at(index)?.get(name) as FormControl;
  }

  getCompanyControl(name: string) {
    return this.company.get(name) as FormControl;
  }

  getRepresentativeControl(name: string) {
    return this.representative.get(name) as FormControl;
  }

  participantsSelect(index: number) {
    const array = this.participants.value
      .filter((_: any, i: number) => i !== index)
      .map((participant: any, i: number) => {
        const name =
          participant.firstName &&
          participant.familyName &&
          `(${participant?.firstName} ${participant?.familyName})`;
        return {
          label: `Participant ${i + 1} ${name}`,
          value: i + 1,
        };
      });
    return array;
  }

  addParticipant() {
    const participantGroup = this.fb.group(
      {
        pack: this.fb.control('', {
          validators: [Validators.required],
          updateOn: 'blur',
        }),
        familyName: this.fb.control('', {
          validators: [Validators.required, Validators.minLength(2)],
          updateOn: 'blur',
        }),
        firstName: this.fb.control('', {
          validators: [Validators.required, Validators.minLength(2)],
          updateOn: 'blur',
        }),
        profession: ['', [Validators.required]],
        speciality: ['', [Validators.required]],
        phoneNumber: [
          '',
          [Validators.required, Validators.pattern('^[- +()0-9]+$')],
        ],
        email: ['', [Validators.required, Validators.email]],
        city: ['', [Validators.required]],
        country: ['', [Validators.required]],
        roommate: [''],
      },
      { updateOn: 'submit' },
    );
    this.participants.push(participantGroup);
  }

  private mapFormToRegistration(): Partial<RegistrationDto> {
    const v = this.groupForm.value;

    return {
      company: {
        address: v.company?.address || '',
        city: v.company?.city || '',
        name: v.company?.name || '',
        ice: v.company?.ice || '',
        country: v.company?.country || '',
      },
      representative: {
        fullName: v.representative?.fullName || '',
        role: v.representative?.role || '',
        phoneNumber: v.representative?.phoneNumber || '',
        email: v.representative?.email || '',
      },
      participants: this.participants.value.map((participant: any) => ({
        familyName: participant.familyName || '',
        firstName: participant.firstName || '',
        profession: participant.profession as Profession,
        speciality: participant.speciality || '',
        phoneNumber: participant.phoneNumber || '',
        email: participant.email || '',
        city: participant.city || '',
        country: participant.country || '',
        pack: participant.pack,
        roommate: Number(participant.roommate),
      })),
      comment: v.comment || '',
      type: RegistrationType.GROUP,
    };
  }

  removeParticipant(index: number) {
    if (this.participants.length > 1) {
      this.participants.removeAt(index);
    }
  }

  selectRoom(index: number, packId: Pack) {
    this.getParticipantControl('pack', index).setValue(packId);
  }

  onSubmit() {
    if (this.groupForm.invalid) {
      this.groupForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    this.reservService
      .createReservation(this.mapFormToRegistration())
      .subscribe({
        next: (data) => {
          this.isSubmitting.set(false);
          this.router.navigate(['confirmation', data]);
        },
        error: (error) => {
          this.isSubmitting.set(false);
          console.error(error);
        },
      });
  }

  isTwinEligible(index: number): boolean {
    this.twinState();
    const currentPack = this.getParticipantControl('pack', index).value;
    if (currentPack === Pack.PACK_SUPP) {
      return true;
    }

    let singleCount = 0;
    let twinCount = 0;

    this.participants.controls.forEach((ctrl, i) => {
      if (i === index) return;
      const pack = ctrl.get('pack')?.value;
      if (pack === Pack.PACK_2 || pack === Pack.PACK_4) {
        singleCount++;
      }
      if (pack === Pack.PACK_SUPP) {
        twinCount++;
      }
    });

    return twinCount < singleCount;
  }

  backPage() {
    this.location.back();
  }
}
